// =============================================================================
// AIM-flavored notification sounds, synthesized via Web Audio API.
// We don't ship sound files — these are generated on demand. Keeps the bundle
// small and scratchy/nostalgic.
// =============================================================================

type SoundName = 'message' | 'mention' | 'join' | 'leave';

let ctx: AudioContext | null = null;
function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      return null;
    }
  }
  return ctx;
}

// Quick "ding" — a short two-tone chime, like an IM ping
function playMessage(audio: AudioContext) {
  const now = audio.currentTime;
  // Tone 1
  envelope(audio, now, 0.18, 880, 0.15);
  // Tone 2 a beat later
  envelope(audio, now + 0.10, 0.18, 1320, 0.15);
}

// Mention — a slightly more attention-grabbing two-note (rising)
function playMention(audio: AudioContext) {
  const now = audio.currentTime;
  envelope(audio, now, 0.22, 660, 0.18);
  envelope(audio, now + 0.13, 0.22, 990, 0.18);
}

// Join — door creak going up (a short ascending sweep)
function playJoin(audio: AudioContext) {
  const now = audio.currentTime;
  sweep(audio, now, 0.28, 220, 660, 0.15);
}

// Leave — short muted "door close" thunk. Two layers:
//   1. A brief filtered-noise click at the moment of impact (the "knock"
//      character — gives the sound its woodiness without being harsh)
//   2. A low triangle pulse at ~140Hz that decays fast (the body of the thunk)
// Total duration ~120ms. Quieter than the join sweep on purpose — leaves
// shouldn't startle.
function playLeave(audio: AudioContext) {
  const now = audio.currentTime;

  // Layer 1 — short noise burst, low-passed for warmth
  const noiseDur = 0.05;
  const bufferSize = Math.max(1, Math.floor(audio.sampleRate * noiseDur));
  const noiseBuffer = audio.createBuffer(1, bufferSize, audio.sampleRate);
  const noiseData = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize); // decay shape
  }
  const noiseSource = audio.createBufferSource();
  noiseSource.buffer = noiseBuffer;
  const noiseFilter = audio.createBiquadFilter();
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.setValueAtTime(800, now);
  const noiseGain = audio.createGain();
  noiseGain.gain.setValueAtTime(0.08, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + noiseDur);
  noiseSource.connect(noiseFilter).connect(noiseGain).connect(audio.destination);
  noiseSource.start(now);
  noiseSource.stop(now + noiseDur + 0.01);

  // Layer 2 — low body thump
  const thumpDur = 0.12;
  const osc = audio.createOscillator();
  const oscGain = audio.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(180, now);
  osc.frequency.exponentialRampToValueAtTime(110, now + thumpDur);
  oscGain.gain.setValueAtTime(0, now);
  oscGain.gain.linearRampToValueAtTime(0.10, now + 0.005);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, now + thumpDur);
  osc.connect(oscGain).connect(audio.destination);
  osc.start(now);
  osc.stop(now + thumpDur + 0.02);
}

function envelope(
  audio: AudioContext,
  startTime: number,
  duration: number,
  frequency: number,
  gain: number
) {
  const osc = audio.createOscillator();
  const g = audio.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, startTime);
  g.gain.setValueAtTime(0, startTime);
  g.gain.linearRampToValueAtTime(gain, startTime + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  osc.connect(g).connect(audio.destination);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.02);
}

function sweep(
  audio: AudioContext,
  startTime: number,
  duration: number,
  fromFreq: number,
  toFreq: number,
  gain: number
) {
  const osc = audio.createOscillator();
  const g = audio.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(fromFreq, startTime);
  osc.frequency.exponentialRampToValueAtTime(toFreq, startTime + duration);
  g.gain.setValueAtTime(0, startTime);
  g.gain.linearRampToValueAtTime(gain, startTime + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
  osc.connect(g).connect(audio.destination);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.02);
}

export function playSound(name: SoundName): void {
  const audio = getContext();
  if (!audio) return;
  // Some browsers suspend audio context until user interaction — try to resume
  if (audio.state === 'suspended') {
    audio.resume().catch(() => {});
  }
  if (audio.state !== 'running') return;
  try {
    switch (name) {
      case 'message': playMessage(audio); break;
      case 'mention': playMention(audio); break;
      case 'join': playJoin(audio); break;
      case 'leave': playLeave(audio); break;
    }
  } catch (err) {
    console.warn('sound play failed', err);
  }
}

// Call once after the user clicks something — primes the AudioContext
// so subsequent plays don't get blocked by autoplay policies.
export function primeAudio(): void {
  getContext();
}

// =============================================================================
// Call ringtones — looping cues for incoming/outgoing DM calls.
// =============================================================================
// A repeating pattern (re-scheduled on an interval) rather than a single
// looping buffer, so it's synthesized like the other cues and stops cleanly.
let ringTimer: number | null = null;

function ringOnce(audio: AudioContext, kind: 'incoming' | 'outgoing') {
  if (audio.state !== 'running') return;
  const now = audio.currentTime;
  if (kind === 'incoming') {
    // A classic double "ring-ring" — two paired chirps.
    envelope(audio, now, 0.32, 660, 0.14);
    envelope(audio, now + 0.18, 0.32, 880, 0.14);
    envelope(audio, now + 0.55, 0.32, 660, 0.14);
    envelope(audio, now + 0.73, 0.32, 880, 0.14);
  } else {
    // Outgoing ringback — a single, calmer low tone.
    envelope(audio, now, 0.5, 440, 0.08);
    envelope(audio, now + 0.35, 0.4, 480, 0.06);
  }
}

/** Start looping a ringtone. Idempotent — restarts cleanly if already ringing. */
export function startRingtone(kind: 'incoming' | 'outgoing'): void {
  stopRingtone();
  const audio = getContext();
  if (!audio) return;
  if (audio.state === 'suspended') audio.resume().catch(() => {});
  ringOnce(audio, kind);
  ringTimer = window.setInterval(
    () => ringOnce(audio, kind),
    kind === 'incoming' ? 2600 : 3200
  );
}

export function stopRingtone(): void {
  if (ringTimer != null) {
    window.clearInterval(ringTimer);
    ringTimer = null;
  }
}
